import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/pages/admin/ConfirmDelete";

export default function AdminPosts() {
  const [posts, setPosts] = useState(null);

  const load = () => api.get("/posts").then(setPosts).catch(() => toast.error("Couldn't load posts."));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    try {
      await api.del(`/posts/${slug}`);
      toast.success("Post deleted.");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div data-testid="admin-posts">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-base text-stone-600">{posts ? `${posts.length} post${posts.length === 1 ? "" : "s"} published` : "Loading..."}</p>
        <Button asChild className="rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700 gap-2">
          <Link to="/admin/posts/new" data-testid="admin-new-post-button">
            <Plus className="h-4 w-4" /> New post
          </Link>
        </Button>
      </div>

      <div className="mt-6 divide-y divide-stone-200 rounded-3xl border border-stone-200 bg-white shadow-sm">
        {posts?.length === 0 && <p className="p-8 text-stone-500" data-testid="admin-posts-empty">No posts yet.</p>}
        {posts?.map((post) => (
          <div key={post.id} className="flex flex-wrap items-center justify-between gap-4 p-5 sm:p-6" data-testid={`admin-post-row-${post.slug}`}>
            <div className="flex items-center gap-4 min-w-0">
              {post.cover_image && (
                <img src={post.cover_image} alt="" className="h-14 w-20 rounded-lg object-cover border border-stone-200 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-heading text-lg font-semibold text-stone-900 truncate">{post.title}</p>
                <p className="mt-1 font-mono text-xs text-stone-400">
                  {post.category} · {format(new Date(post.created_at), "MMM d, yyyy")} · {post.read_time}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/blog/${post.slug}`} target="_blank" className="rounded-full p-2 text-stone-400 hover:text-stone-900" aria-label="View" data-testid={`admin-post-view-${post.slug}`}>
                <ExternalLink className="h-4 w-4" />
              </Link>
              <Link to={`/admin/posts/${post.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:border-stone-900" data-testid={`admin-post-edit-${post.slug}`}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
              <ConfirmDelete
                title={`Delete "${post.title}"?`}
                onConfirm={() => remove(post.slug)}
                triggerTestId={`admin-post-delete-${post.slug}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </ConfirmDelete>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
